<template lang="pug">
video.video-js.vjs-city-offer.vjs-show-big-play-button-on-pause.minimalistic(
    ref="player"
    controls
    playsinline
    preload="auto"
    :style="playerStyle"
)
    source(:src="src" :type="mime")
</template>

<script>
import videojs from 'video.js';

/**
 * Single-source videojs player. The parent re-keys this component when the source
 * changes (e.g. MP4 -> HLS after conversion), so Vue fully destroys + recreates it.
 * That gives a clean videojs init per source — switching a live player from a
 * progressive MP4 to an MSE/HLS source corrupts the pipeline
 * (DEMUXER_ERROR_COULD_NOT_OPEN), whereas a fresh init plays HLS fine.
 */
export default {
    name: 'HlsVideoPlayer',
    props: {
        src: {
            type: String,
            required: true
        },
        mime: {
            type: String,
            default: ''
        },
        width: {
            type: Number,
            default: 150
        },
        height: {
            type: Number,
            default: 150
        },
        muted: {
            type: Boolean,
            default: true
        },
        autoplay: {
            type: Boolean,
            default: false
        },
        // Fluid lets videojs derive the box from an aspect-ratio padding and position the
        // <video> inside it. That is right for the tile, but in a dialog the player has a
        // known size, and the padding box is what pushed the picture out of the frame.
        fluid: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            player: null
        };
    },
    computed: {
        // videojs turns this element into the tech INSIDE the player it builds, and an
        // inline px size survives that move - which pins the picture to a fixed box and
        // defeats any max-height. Outside fluid mode the size travels through the videojs
        // options instead, so the tech can follow its container.
        playerStyle() {
            if (!this.fluid) {
                return {};
            }

            return { width: this.width + 'px', height: this.height + 'px' };
        }
    },
    watch: {
        // Swap the source IN PLACE on the same player (e.g. MP4 -> HLS once conversion
        // finishes). Re-creating the player via a :key destroys the <video> and the new
        // instance fails to mount (videojs vs Vue own the same DOM node) — leaving an empty
        // tile. videojs.src() switches tech (progressive -> VHS/HLS) without that conflict.
        src(newSrc, oldSrc) {
            if (this.player && newSrc && newSrc !== oldSrc) {
                this.player.src({ src: newSrc, type: this.mime || undefined });
            }
        }
    },
    mounted() {
        if (!this.$refs.player) return;
        const options = {
            muted: this.muted,
            language: 'en',
            preload: 'auto',
            // 'any' lets videojs do the whole dance itself: try with sound, and if the
            // browser refuses, mute and start anyway. Calling play() by hand raced the
            // source loading and often ended on a frozen first frame.
            autoplay: this.autoplay ? 'any' : false,
            fluid: this.fluid,
            width: this.width,
            height: this.height,
            html5: {
                vhs: {
                    overrideNative: true,
                    limitRenditionByPlayerDimensions: false,
                    useDevicePixelRatio: true
                },
                nativeAudioTracks: false,
                nativeVideoTracks: false,
                useBandwidthFromLocalStorage: true
            },
            controlBar: {
                PlayToggle: false,
                RemainingTimeDisplay: false,
                pictureInPictureToggle: false,
                volumePanel: false
            }
        };

        this.player = videojs(this.$refs.player, options);
        this.player.httpSourceSelector();
        this.player.on('play', () => {
            this.$emit('play');
        });
        this.player.ready(() => {
            this.player.tech_.off('dblclick');
        });
    },
    beforeDestroy() {
        if (this.player) {
            this.player.dispose();
            this.player = null;
        }
    },
    methods: {
        pause() {
            if (this.player) {
                this.player.pause();
            }
        },
        // Start playback from the outside - used once the dialog has finished opening.
        // The videojs `autoplay` option fires while the dialog is still hidden behind its
        // transition, which some browsers refuse; this is the second, reliable attempt.
        play() {
            if (!this.player || !this.player.paused()) {
                return;
            }

            const started = this.player.play();
            if (started && started.catch) {
                started.catch(() => {
                    if (!this.player) return;
                    this.player.muted(true);
                    this.player.play();
                });
            }
        }
    }
};
</script>
